// GitLab WebUI issue #30 / M001-D023 §7 (Share-panel template), version 0.9
// one-list redesign (2026-08-21). Mantis #1178 (one-list restructure), #1179
// (inline role editing), #1180 (add-people-groups combobox), #1181 (General
// access rename/reposition).
// Test: T-MCRO-020 case O (amended) / case P (new) — MCRO001-F0020 /
// M001-F0232 (choreo_sheet); M004-F0604 / T-M004-0016 item 4 (choreo_media,
// same component, D023 §12.1).
//
// Version 0.11 (2026-08-22, M001-D023 §7.4/§7.4.1, M001-F0297, Mantis #1194/
// #1195/#1196): "General access" (v0.9's §7.4) is retired as a named, opened-
// separately fieldset and replaced by "Quick setup" — five one-click buttons
// (Revoke, Publish, Organisation, Link, Group) rendered ABOVE the list
// (QuickSetupRow below). Buttons 1/3/4/5 call exactly the functions the
// retired fieldset already called (replaceAccess/setBroadAudience/
// createOrRotateLink/a focus call) — no wire change. Button 2 (Publish) is
// the one new orchestration: M001-F0297's compound action, pure client-side,
// no new endpoint (§7.4.1). The standalone "Publish" section and "Community
// contribution policy" section that used to sit below General access on
// `MusicChoreography.js`'s Access tab are retired by this same version —
// see that file's own comments for the compound-Publish wiring and the
// ceiling→community-policy mapping (MCRO001-F0014, owned by
// `MCRO001-D003`, not this file — this file stays resourceType-generic).
// Test: T-MCRO-020 case Q / T-RES-SHARE-001 case Z.
//
// Version 0.9 rewrites the panel's presentation in full per the owner's own
// diagnosis (D023 §7.0): "what is 'Who can access'... it's the presentation,
// it's more like this is how the DB is structured". The wire contract is
// UNCHANGED — AUDIENCES, CEILINGS, principals, link, inheritedAccess,
// allowedAudienceCeilings and every endpoint below are exactly as they were;
// only the render tree changed. See D023 §7.5 for the terminology mapping
// this file implements, and §7.7 for what every removed 0.7 control maps to.
//
// Mantis #1186 / M001-F0232 case W, M001-F0296 cases X-Y (2026-08-21):
// M001-D023 v0.10 §5.3 adds a fourth ceiling, `manage`, to the wire
// `CEILINGS` enum. This IS the "future revision" §7.6 item 1 warned about —
// Core will start returning `manage` on `choreo_sheet` grants, and this MR
// ships to production FIRST (WebUI issue #1186 is the explicit
// must-ship-before-Core companion to Core #1183/#1184): an un-updated
// `isSharingAccessState`/`validPrincipal` would fail closed on the whole
// access payload the instant Core emits it. Two changes:
//   1. `manage` joins `CEILINGS`/`DEFAULT_CEILING_LABELS` with the exact
//      §5.3/§5 label "Can manage access". The inline role `<select>` (§7.2.3
//      kind 1) and the combobox's ceiling `<select>` already source their
//      OPTIONS from the resource's own `allowedAudienceCeilings.specific`
//      (server-declared), never from this constant directly — so a Core that
//      allows `manage` populates it with zero further change here, and a
//      Core that does not allow it (every adapter except `choreo_sheet`,
//      §5.3 "Scope") never offers it. This is the client half of §5.3's own
//      no-escalation invariant: WebUI presents only what the server permits.
//   2. Forward-compatibility for ceiling #5 (and beyond): ceiling validation
//      below no longer hard-rejects an unrecognised wire value — see
//      `isCeilingValue` and its comment. A row whose OWN ceiling is not in
//      the resource's current `allowedAudienceCeilings.specific` (whether
//      that ceiling is `manage` on a deployment where it is no longer
//      offered, or a wholly unknown future value) renders as a disabled,
//      reason-carrying control (§7.2.3 kind 2, reused) rather than either
//      vanishing or offering itself as a selectable escalation target.
//
// Version 0.12 (2026-08-22, owner product feedback on the live Access panel —
// no fresh Feature/Test ID allocated in this MR; this repo is not the
// design-authority repo (studioaeye-choreo/M001-D023 lives there) and this
// change was routed directly as an owner-reported visual/presentation defect,
// not a new behaviour. Amend M001-D023 §7.1/§7.4/§7.4.1 to match the three
// points below the next time that doc is touched):
//   1. LAYOUT: Quick setup moves from ABOVE "People with access" to the
//      BOTTOM of the panel — after the list AND after "Add people, groups"
//      (previously §7.1 put it first; the owner's own read was "put the
//      buttons along the bottom"). QuickSetupRow no longer takes a DOM ref
//      at all: `quickSetupRef` existed only so `focusQuickSetup()` could
//      route the General-access row's retired "Change" button here, and
//      both were removed together in version 0.13 — see that version's own
//      file-header note.
//   2. PRESENTATION: all five Quick-setup buttons (Revoke, Publish,
//      Organisation, Link, Group) now share one class, `mc-quick-setup-button`
//      (MusicChoreography.css), so they read as five peer actions instead of
//      three different treatments (a ghost-link Revoke, a solid-filled
//      Publish, and three bare, unclassed `<button>`s rendering in the
//      browser's own UI font because an unclassed `<button>` does not inherit
//      `font-family`). Revoke keeps a danger colour accent via the existing
//      `mc-text-danger` class layered on top (see the combined-selector CSS
//      rule); it no longer has a different shape from its siblings.
//   3. DISABLED REASONS ARE NOW VISIBLE: the disabled-reason text for Revoke/
//      Publish/Organisation/Link used to be `mc-visually-hidden` (screen
//      reader only) — the owner's own "why is Link not clickable?" is that
//      gap: a sighted user got no explanation at all. QuickSetupRow now
//      collects each disabled button's reason text, deduplicates identical
//      text (a draft/unpublished Sheet disables Organisation AND Link for the
//      literal same reason, "Publish this Sheet first." — Core's
//      choreographySheetSharingAdapter offers no `link` ceilings and no
//      `view` scope ceiling until the Sheet is published), and renders each
//      distinct reason once, visibly, in `.mc-quick-setup-reasons` below the
//      button row. `aria-describedby` still points at the exact visible
//      element carrying a button's reason — shared across buttons when the
//      text is shared — so a screen-reader user is told the same thing a
//      sighted user can already see, never something different.
//
// Layout (D023 §7.1, version 0.12, top to bottom, nothing between):
//   1. "People with access" — one list, one row per source of access (§7.2).
//   2. "Add people, groups" — the combobox, directly beneath the list (§7.3).
//   3. "Quick setup" — five one-click buttons: Revoke, Publish, Organisation,
//      Link, Group (§7.4/§7.4.1), plus any active disabled-reason text,
//      positioned LAST, along the bottom of the panel (point 1 above).
//      Quick setup's Group button still opens the "Add people, groups"
//      combobox above it.
//   4. Nothing else. Copy link is a row-level action (§7.2.4), never a
//      standalone button. There is no longer a "General access" fieldset, a
//      standalone "Publish" section or a "Community contribution policy"
//      section on this tab.
//
// Version 0.13 (2026-08-23, M001-D023 §7.2.3/§7.1, M001-F0232 extended,
// T-RES-SHARE-001 case AB — no new Feature ID, per D023 §13.6's own
// traceability table): "one row contract" — retires editability kind 3
// ("routed control"/the General-access row's "Change" button) outright.
// Two direct owner complaints, one root cause: "the org is not the same as
// the other! for user you have a drop where you have done something
// completely different for org" and "'Anyone in the organisation' has no
// remove! ...it['s] full of inconsistency." A row's mechanics must never
// vary with how the grant happens to be stored (named principal vs. broad
// policy vs. link):
//   1. The General-access row's ceiling cell is now the SAME inline
//      <select> a named/group row already has (buildAccessRows below),
//      populated from `allowedAudienceCeilings[access.audience]` — NEVER
//      `.specific`, which governs named/group rows only. Selected/compared
//      by RANK (`CEILING_RANK`/`lowestRankedCeiling`, already implemented
//      for Quick setup's own default selection), never array position, so a
//      future reordering of the server's array cannot silently change what
//      a click grants. Changing it calls the identical `replaceAccess` a
//      named row's <select> already calls (`editGeneralCeiling` below).
//   2. Remove is now offered on the General-access row for EVERY audience
//      (`scope`/`public` included, not only `link`), routed through the
//      SAME generic `removeRule` mechanism already used for named/group
//      rows — one code path, not a second mechanism. This also corrects
//      `removeRule`'s previously-unconditional `kind === 'general' →
//      deleteLink()` branch (a live defect masked only by no button
//      previously reaching it for `scope`/`public`) to test
//      `access.audience === 'link'` first. The `link` row's Remove keeps
//      routing to `deleteLink` — a named, justified exception under the same
//      "Remove" label, not a different mechanism with no stated reason.
//      CORRECTED post-review (lead read Core `origin/main` @ b9c37722,
//      `resourceSharingPhase2.service.ts`): `deleteLink()` (:886) calls
//      `revokeChildren` (:916, :427) with no exclusion list, which
//      unconditionally revokes EVERY grant and every non-redeemed
//      invitation on the policy (:458-462), then (:921-925) unconditionally
//      sets `audience: 'private'` — in EVERY case, not only when the link
//      happens to be the sole row. This is materially wider than "a token
//      invalidated underneath" (this file's own earlier, inaccurate
//      characterisation) — removing a link with named people or groups
//      still attached revokes them too. `DeleteRuleDialog` below states
//      this plainly, sized to the real state, rather than in a comment only
//      the code author reads.
//      STRUCK by version 0.15 (2026-08-24, M001-D023 §8.8.5, DEPLOY-GATED —
//      see file-header note): this was an accurate reading of Core
//      `origin/main` @ b9c37722 when written, not a description of current
//      behaviour — Core issue #72 has since scoped `deleteLink()` to the
//      link alone. Left standing, not deleted, per this file's own
//      superseded-ruling convention; see the version-0.15 note above for
//      what `DeleteRuleDialog` states now.
// Also fixed in this same change, same file (production defect, not a D023
// §7 presentation item, but corrects §7.4.1's already-normative
// no-escalation/"never silently missing" principle Publish had not yet
// honoured): Publish now disables itself with a visible reason whenever
// `allowedAudienceCeilings.public` is empty (every production choreo_sheet
// today — no Sheet has ever had a `safeProjection`, D023 §8.7.1 — so
// Publish's own `audience: 'public'` write was unconditionally rejected
// server-side). Driven off the server's own advertised ceilings, never a
// hardcoded assumption, so the button re-enables itself with no code change
// the moment a Sheet's projection starts advertising a safe view — an open
// owner decision this fix does not make or anticipate.
//
// Version 0.14 (2026-08-24, two live owner rulings, this file only — see
// this MR): "one list / Google-Docs-style" (the standing ruling behind the
// whole 0.9 redesign, file header above) extended to the ONE gap it had
// missed:
//   1. Owner, verbatim: "I click the [link] and I get a second box with
//      text that dose not make scene with another buttom I need to click. I
//      expect when I click [link] a row is added and from that row I modify
//      want I wany." Quick setup's Link button ALREADY one-click-called
//      `createOrRotateLink` directly (version 0.11) — no pre-creation
//      dialog exists anywhere in this file (`LinkDialog` opens only via the
//      row's own "Manage", already an existing link). The actual "second
//      box" was `LinkResultDialog` — a modal this file popped up AFTER a
//      successful create/rotate, forcing "Copy link" / "Done" as its own
//      extra click before the owner could do anything else. Retired
//      outright: `createOrRotateLink`'s success path now only caches the
//      one-time secret (`linkShareUrl`, unchanged wire/security contract,
//      see "KNOWN DEGRADED BEHAVIOUR" below) and calls the same `closeDialog`
//      every other action in this file already calls. The row's own "Copy
//      link" action (already implemented, already reads `linkShareUrl`
//      first) is now the ONLY surface for the just-created/rotated secret —
//      one mechanism, not two, matching every other row action in this
//      panel. `LinkDialog` keeps its one remaining, narrower job — rotate/
//      change TTL on an ALREADY-EXISTING link via "Manage" — evaluated
//      against collapsing TTL into an inline row control per the ruling's
//      own spirit ("from that row I modify what I want"); kept as the
//      single remaining dialog for now (not eliminated) because the access
//      table already carries four columns and TTL is a rare action, and the
//      ruling itself names this an acceptable fallback when the alternative
//      is a crowded row. CREATION itself never passes through it, which is
//      the ruling's actual requirement.
//   2. Owner hit a specific server 400 (`RESOURCE_SHARE_ADAPTER_POLICY_DENIED`
//      class) replaced by this file's own generic "The link could not be
//      created. No access changes were made." — costing a full diagnostic
//      session reading the wrong layer. Every mutation catch below that
//      previously hardcoded a generic string on the non-409 branch now shows
//      the server's own `message` first (`serverErrorMessage` helper,
//      checking the nested AppError shape this file's own fixtures and
//      Core's real serializers use, `data.error.message`, then the flatter
//      `data.message` some other endpoints in this app use, matching the
//      dual-check convention already established elsewhere — e.g.
//      BookingKeeperDashboard.js's `apiError`) — the generic text is now
//      strictly a fallback for when the server genuinely sent no message at
//      all (a network failure that never reached it), never a translation of
//      a CODE into new hardcoded prose. 409's own reload-and-say-"changed
//      elsewhere" behaviour is UNCHANGED — a conflict is a routing decision,
//      not a missing explanation.
//
// Version 0.15 (2026-08-24, M001-D023 §8.8.5/§8.8.6, M001-F0232 extended,
// T-RES-SHARE-001 case AF — WebUI-side follow-up to two already-implemented
// Core fixes, DEPLOY-GATED, see this MR's own description before merging):
//
// Core issue #72 (§8.8.5, case AD) scoped `deleteLink()` to revoke ONLY the
// active link; issue #76 (§8.8.6, case AE) did the same for `createLink()`.
// Both are IMPLEMENTED on `studioaeye-Core` `origin/main` (verified before
// this change) but had NOT yet reached production when this MR was opened —
// this MR must not merge/deploy ahead of that Core release, or the two
// warnings below would be removed while the wipe they describe was still
// real: silent data loss, not a copy fix. See the MR body for the gate.
//
//   1. QuickSetupRow's pre-create "Replace every configured rule?" dialog
//      (`needsReplaceConfirm`/`confirmLink`, added when `createLink()` really
//      did wipe every named grant/invitation on click) is REMOVED outright,
//      not reworded — once §8.8.6 is live, clicking Link never removes
//      anyone else's access, so there is nothing left to confirm. Link
//      becomes unconditionally one-click for every prior `access.audience`,
//      completing the owner's version-0.14 "one list / Google-Docs-style"
//      ruling above rather than leaving one audience-gated exception to it.
//   2. DeleteRuleDialog's link-row copy no longer claims removing the link
//      also revokes named people/invitations or forces the Sheet Private —
//      once §8.8.5 is live, it does neither; named grants and pending
//      invitations survive verbatim. `namedCount`/`invitationCount` (only
//      ever computed to size that now-false warning) are removed with it.
//      The genuine last-rule case (`lastRule`, the link IS the only row) is
//      UNCHANGED — removing the only access mechanism still correctly reads
//      "Removing the last rule makes this Private", true both before and
//      after §8.8.5/§8.8.6 since nothing else exists to preserve either way.
//
// KNOWN DEGRADED BEHAVIOUR (do not silently "fix" by fabricating data):
// - D023 §9.2 requires the access-explain response to name the GROUP a named
//   individual grant overrides ("comment — set individually, overrides
//   LindyHop-Teachers"). mediaAccessExplain.service.ts does not return that
//   group name today (Core issue #56 bullet 5, tracked separately, NOT this
//   ticket's scope). ProvenanceLine below renders "set individually" and
//   simply omits the "overrides X" clause when the API doesn't supply it —
//   see ProvenanceLine's own comment.
// - Link TTL (`ttlSeconds` on create/rotate, `expiresAt` in the response) is
//   specified in D023 §8.8 but was still Draft (studioaeye-Core MR !433) at
//   the time this shipped. LinkDialog feature-detects it (tries ttlSeconds,
//   falls back once to the pre-!433 request shape on the exact rejection the
//   old backend returns) rather than assuming either shape — see
//   createOrRotateLink's own comment.
// - The bearer link secret is one-time-disclosed by the wire contract (D023
//   §11, unchanged) — there is no "fetch the existing link value" endpoint.
//   The list row's "Copy link" action can only copy a secret this session
//   already holds (just created/rotated); when none is cached it opens
//   "Manage" (rotate) instead of fabricating or re-requesting a value the API
//   does not return. This is a direct, honest consequence of the unchanged
//   security contract, not a gap introduced by this redesign.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';

export const AUDIENCES = ['private', 'link', 'specific', 'scope', 'public'];
// D023 v0.10 §5.3: `manage` is opt-in per adapter (only `choreo_sheet` offers
// it as of v0.10) and never offered here directly — see the file-header note.
export const CEILINGS = ['view', 'contribute', 'edit', 'manage'];
// Owner review of #1194 (2026-08-22): mirrors studioaeye-Core's
// `RESOURCE_SHARING_CEILING_RANK` (`src/services/resourceSharing.types.ts:26-31`,
// `{ view: 0, contribute: 1, edit: 2, manage: 3 }`, backend-internal — Core is
// not an importable package from this repo). Core's own comment there is
// explicit: rank "must go through this table, never Array.indexOf or enum
// position." `CEILINGS` above is the one already-existing, already-correct
// low-to-high ordering this panel uses (D023 §5's cumulative table — each
// ceiling includes everything before it) — this derives the rank FROM that
// constant rather than hand-writing a second ordering that could drift from
// it. `allowedAudienceCeilings[audience]` (the SERVER array) must never be
// read by position: for `choreo_sheet`, `scope` on a draft Sheet is exactly
// `['edit', 'manage']` (no `view` — Core does not offer view-only access to
// a Sheet with no published revision to view), so `[0]` silently selects
// `edit`, not the "everyone can see it" the Organisation button promises —
// a one-click org-wide edit grant behind a view-only label. Rank-based
// selection picks the true lowest ceiling regardless of the server's array
// order.
const CEILING_RANK = Object.fromEntries(CEILINGS.map((ceiling, index) => [ceiling, index]));
function lowestRankedCeiling(offered) {
  const ranked = (offered || []).filter((ceiling) => Object.prototype.hasOwnProperty.call(CEILING_RANK, ceiling));
  if (ranked.length === 0) return undefined;
  return ranked.reduce((lowest, candidate) => (CEILING_RANK[candidate] < CEILING_RANK[lowest] ? candidate : lowest));
}
const DELIVERY_STATES = ['pending_delivery', 'delivered', 'delivery_failed'];
const dialogStack = [];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_TTL_DAYS = 1;
const MAX_TTL_DAYS = 90;
const DEFAULT_TTL_DAYS = 7;
const DAY_SECONDS = 86400;
const COMBOBOX_DEBOUNCE_MS = 200;
const COMBOBOX_MIN_CHARS = 2;

// D023 §4/§5 generic defaults. Only used when a resource's own consequenceLabels
// response omits an entry — the normal path always uses the adapter's own
// labels (both registered adapters supply consequenceLabels() on every
// GET .../access response today), never these defaults.
const DEFAULT_AUDIENCE_LABELS = {
  private: { label: 'Private', consequence: 'Removes named access and any link; independent owner access remains.' },
  link: { label: 'Anyone with the link', consequence: 'An unlisted link lets anyone who has it view this.' },
  specific: { label: 'Specific people', consequence: 'Only the people and groups you choose are eligible.' },
  scope: { label: 'Anyone in the organisation', consequence: 'Active members of your organisation are eligible.' },
  public: { label: 'Everyone', consequence: 'This becomes publicly discoverable.' },
};
const DEFAULT_CEILING_LABELS = {
  view: { label: 'Can view', consequence: 'Can view only.' },
  contribute: { label: 'Can contribute', consequence: 'Can view and contribute.' },
  edit: { label: 'Can edit', consequence: 'Can view, contribute and edit.' },
  // D023 v0.10 §5.3: "View/contribute/edit plus add, remove and change the
  // ceiling of other principals on this resource" — "it still never implies
  // ... changing any capability other than who has access to this one
  // resource." Opt-in per adapter; only `choreo_sheet` offers it (§5.3).
  manage: { label: 'Can manage access', consequence: 'Can view, contribute and edit, plus add, remove and change other people’s access to this resource — nothing beyond that.' },
};
// D023 §7.4 (version 0.11): the three broad audiences Quick setup's
// Organisation/Link/Publish buttons can reach directly. `private` is reached
// only via Revoke (an unconditional clear, never a fourth "Restricted"
// choice — that v0.9 radio and its fieldset are retired) and `specific` is
// never a quick-setup target (unchanged from v0.9 — reached only through
// Group → the combobox).
const BROAD_AUDIENCES = ['link', 'scope', 'public'];

const responseData = (response) => response?.data?.data || response?.data;
const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isUtcRfc3339 = (value) => typeof value === 'string'
  && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/.test(value)
  && !Number.isNaN(Date.parse(value));
const idempotencyKey = (() => {
  let sequence = 0;
  return (prefix) => `${prefix}-${Date.now().toString(36)}-${++sequence}`;
})();

// ---------------------------------------------------------------------------
// Wire validation — UNCHANGED from 0.7 for AUDIENCES (D023 §7.6 item 1: "No
// wire/API change" held through 0.9). Version 0.10 (§5.3) is the "future
// revision" that bullet itself deferred: it adds `manage` to `CEILINGS`, and
// this MR ships before Core can emit it. A hard `CEILINGS.includes(...)`
// check on every ceiling field would fail the WHOLE access payload closed —
// not just the one unrecognised grant — the instant a resource carries a
// ceiling this exact deployment doesn't know about yet (the fifth ceiling,
// the sixth, ...). `isCeilingValue` replaces those checks with a structural
// one (non-empty string): CEILINGS/DEFAULT_CEILING_LABELS still gate what
// gets a real label and what this panel ever OFFERS as a selectable option
// (never more than the server's own allowedAudienceCeilings, see the file
// header), but an unrecognised value on an already-existing grant degrades to
// a visible, non-editable row (ceilingCopy's fallback + buildAccessRows'
// ceilingOfferable check below) instead of vanishing the entire panel.
// ---------------------------------------------------------------------------

const isCeilingValue = (value) => typeof value === 'string' && value.length > 0;

const validPrincipal = (principal) => isObject(principal)
  && ['user', 'group', 'invited_identity'].includes(principal.principalType)
  && typeof principal.displayLabel === 'string'
  && isCeilingValue(principal.ceiling)
  && (principal.expiresAt === undefined || isUtcRfc3339(principal.expiresAt))
  && (principal.principalType === 'invited_identity'
    ? typeof principal.invitationId === 'string' && UUID.test(principal.invitationId)
      && DELIVERY_STATES.includes(principal.deliveryState)
      && isUtcRfc3339(principal.expiresAt)
    : typeof principal.principalId === 'string');

const validInheritedAccess = (value) => value === null || (isObject(value)
  && typeof value.displayLabel === 'string' && value.displayLabel.length > 0
  && typeof value.accessLabel === 'string' && value.accessLabel.length > 0
  && value.editable === false && value.source === 'resource_authority'
  && Array.isArray(value.actions) && value.actions.every((action) => typeof action === 'string'));

export function isSharingAccessState(value) {
  return isObject(value)
    && Number.isInteger(value.version) && value.version >= 0
    && AUDIENCES.includes(value.audience)
    && isCeilingValue(value.defaultCeiling)
    && Array.isArray(value.principals) && value.principals.every(validPrincipal)
    && isObject(value.link) && typeof value.link.active === 'boolean'
    && Number.isInteger(value.link.version) && value.link.version >= 0
    && isObject(value.allowedAudienceCeilings)
    && AUDIENCES.every((audience) => Array.isArray(value.allowedAudienceCeilings[audience])
      && value.allowedAudienceCeilings[audience].every((ceiling) => isCeilingValue(ceiling)))
    && isObject(value.capabilities)
    && isObject(value.consequenceLabels)
    && Object.prototype.hasOwnProperty.call(value, 'inheritedAccess')
    && validInheritedAccess(value.inheritedAccess);
}

export function sharingAccessFromResponse(response) {
  const value = responseData(response);
  if (!isSharingAccessState(value)) throw new Error('Invalid resource access response');
  return value;
}

const mutationFromResponse = (response, previous) => {
  const value = responseData(response);
  if (!isObject(value)
    || !Number.isInteger(value.version) || value.version < 1
    || !AUDIENCES.includes(value.audience)
    || !isCeilingValue(value.defaultCeiling)
    || !Array.isArray(value.principals) || !value.principals.every(validPrincipal)
    || !isObject(value.link) || typeof value.link.active !== 'boolean'
    || !Number.isInteger(value.link.version) || value.link.version < 0) {
    throw new Error('Invalid resource access mutation response');
  }
  return { ...previous, ...value };
};

const principalKey = (principal) => principal.principalType === 'invited_identity'
  ? `invited_identity:${principal.invitationId}`
  : `${principal.principalType}:${principal.principalId}`;

const principalInput = (principal) => {
  if (principal.principalType === 'invited_identity') {
    return {
      principalType: 'invited_identity',
      invitationId: principal.invitationId,
      ceiling: principal.ceiling,
      expiresAt: principal.expiresAt,
    };
  }
  return {
    principalType: principal.principalType,
    principalId: principal.principalId,
    ceiling: principal.ceiling,
    ...(principal.expiresAt ? { expiresAt: principal.expiresAt } : {}),
  };
};

// ---------------------------------------------------------------------------
// Task-first, plain-language labels. NEVER "Audience" / "Permission ceiling" /
// "baseline" / "Public-discovery state" / "Inherited access" — those are the
// literal internals-shaped labels GitLab issue #30 rejects. Ceiling/audience
// human copy comes from the resource's own consequenceLabels whenever present.
// ---------------------------------------------------------------------------

const audienceCopy = (access, value) => access?.consequenceLabels?.audiences?.[value] || DEFAULT_AUDIENCE_LABELS[value]
  || { label: 'Unavailable', consequence: '' };
// Forward-compat fallback (Mantis #1186 / M001-F0296 case X): a ceiling this
// deployment does not recognise — no adapter consequenceLabels entry AND no
// DEFAULT_CEILING_LABELS entry — renders its own raw wire value rather than
// the generic "Unavailable" used for a genuinely missing audience label.
// "Unavailable" reads as an error; a manager looking at someone's access row
// needs to see WHAT was stored, even if this app doesn't yet have a friendly
// name for it (§7.2.3's "never silently missing" principle, extended to an
// enum value instead of a whole control).
const ceilingCopy = (access, value) => access?.consequenceLabels?.ceilings?.[value] || DEFAULT_CEILING_LABELS[value]
  || { label: `Unrecognised access level (${value})`, consequence: "This access level isn't recognised by this version of the app." };
const audienceLabel = (access, value) => audienceCopy(access, value).label;
const ceilingLabel = (access, value) => ceilingCopy(access, value).label;

// Owner ruling 2026-08-24 (version 0.14, file header): a server 400
// (`RESOURCE_SHARE_ADAPTER_POLICY_DENIED` class) was hidden behind this
// file's own generic "could not be created" copy, costing a full diagnostic
// session. Every AppError response this file's own fixtures/Core's real
// serializers use carries a human `message` nested under `error`
// (`{ success: false, error: { code, message } }` — see
// ResourceSharePanel.test.js's `rejection()`); the flatter `data.message`
// some other endpoints in this app use is checked second, matching the
// dual-check convention already established elsewhere (e.g.
// BookingKeeperDashboard.js's `apiError`). `fallback` is used ONLY when the
// response carries no message at all (a network failure that never reached
// the server) — this never translates a CODE into new hardcoded prose; the
// server's own words are the truth surface.
const serverErrorMessage = (cause, fallback) => {
  const message = cause?.response?.data?.error?.message || cause?.response?.data?.message;
  return typeof message === 'string' && message.length > 0 ? message : fallback;
};

// M004-F0663 (WebUI #130): a failed share/access WRITE must never be silent
// and must be traceable. Returns the server's own words verbatim plus the Core
// error code and the request id (x-request-id header, or error.requestId in the
// body) so the banner can show them and the failure can be matched to server
// logs. `fallback` is used only when the server sent no message at all.
const requestIdOf = (cause) => {
  const headers = cause?.response?.headers || {};
  const fromHeader = typeof headers.get === 'function' ? headers.get('x-request-id') : (headers['x-request-id'] || headers['X-Request-Id']);
  const body = cause?.response?.data || {};
  const sent = cause?.config?.headers;
  const sentId = (typeof sent?.get === 'function' ? sent.get('X-Request-Id') : sent?.['X-Request-Id']) || '';
  // Core's AppError body carries no id except `reference` on a 5xx; the id the
  // WebUI itself sent (and Core logs as x-request-id) is the trace handle.
  const id = fromHeader || body?.error?.requestId || body?.requestId || body?.reference || sentId;
  return typeof id === 'string' && id.length > 0 ? id : '';
};
export const writeFailure = (cause, fallback) => {
  const body = cause?.response?.data || {};
  const code = body?.error?.code || (typeof body?.error === 'string' ? body.error : '') || body?.code || '';
  const failure = {
    message: serverErrorMessage(cause, fallback),
    code: typeof code === 'string' ? code : '',
    requestId: requestIdOf(cause),
    status: cause?.response?.status || null,
  };
  // eslint-disable-next-line no-console
  console.error('[resource-sharing] write failed', failure);
  return failure;
};
const failureText = (error) => (typeof error === 'string' ? error : error?.message || '');
// One-line form for panels that keep a plain-string error (canvas scope panel,
// member preset): the server's message verbatim, then code and request id.
export const failureLine = (failure) => {
  const detail = [failure.code && `Code ${failure.code}`, failure.requestId && `Request ${failure.requestId}`].filter(Boolean).join(' · ');
  return detail ? `${failure.message} (${detail})` : failure.message;
};

// M004-F0663: "success" is only claimed when Core's own response reflects the
// change that was asked for. A 2xx whose body shows the old state is not a
// save. Named user/group principals must be present with the requested ceiling
// (and absent when removed); the audience must be the requested one; the
// general ceiling is checked only when this request is the one changing it.
const NOT_CONFIRMED = 'Core did not confirm the change — nothing was saved. The latest access was reloaded.';
const changeUnconfirmed = (desired, next, previous) => {
  if (!desired || !next) return false;
  if (desired.audience && next.audience !== desired.audience) return true;
  if (desired.defaultCeiling && previous && desired.defaultCeiling !== previous.defaultCeiling && next.defaultCeiling !== desired.defaultCeiling) return true;
  const named = (list) => (list || []).filter((item) => item.principalType !== 'invited_identity');
  const invited = (list) => (list || []).filter((item) => item.principalType === 'invited_identity');
  const nextNamed = new Map(named(next.principals).map((item) => [`${item.principalType}:${item.principalId}`, item]));
  const wantedNamed = named(desired.principals);
  if (nextNamed.size !== wantedNamed.length) return true;
  if (wantedNamed.some((item) => nextNamed.get(`${item.principalType}:${item.principalId}`)?.ceiling !== item.ceiling)) return true;
  return invited(next.principals).length < invited(desired.principals).length;
};

// A deterministic, action-derived reason — NEVER user-typed. GitLab issue #30:
// "audit-reason machinery either derived automatically or removed from the
// user's path." The wire `reason` field is real and required (10-500 chars,
// D023 §8.1); this always produces a description of the action taken, never a
// justification prompt. Exported (GitLab issue #41) so other admin-authority
// surfaces mutating access outside this file — ChoreographyAccessPanel in
// MusicChoreography.js — derive their audit reason the same way instead of
// inventing a second convention.
export function reasonFor(text) {
  const trimmed = String(text).trim();
  const padded = trimmed.length >= 10 ? trimmed : `${trimmed} — panel action.`;
  return padded.slice(0, 500);
}
const namesList = (entries) => entries.map((entry) => entry.displayLabel).join(', ');

function expiresAtFromDays(days) {
  return new Date(Date.now() + days * DAY_SECONDS * 1000).toISOString();
}
function formatExpiry(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// D023 §7.2.1 — deterministic row ordering (testable in isolation; this is
// the one function a mutation test targets to prove row-order assertions can
// actually fail):
//   tier 0: inherited row, if inheritedAccess !== null (at most one)
//   tier 1: General-access row, if audience ∈ {link, scope, public} (at most one)
//   tier 2: direct-access (user) + pending-invitation rows, alpha by
//           displayLabel (case-insensitive), principalKey as tiebreak
//   tier 3: group rows, same sort as tier 2
// ---------------------------------------------------------------------------

function compareRowsByDisplayLabel(a, b) {
  const byLabel = a.who.toLowerCase().localeCompare(b.who.toLowerCase());
  return byLabel !== 0 ? byLabel : a.key.localeCompare(b.key);
}

export function buildAccessRows(access) {
  if (!access) return [];
  const rows = [];
  if (access.inheritedAccess) {
    rows.push({
      key: 'inherited',
      kind: 'inherited',
      who: access.inheritedAccess.displayLabel,
      source: null,
      ceilingText: access.inheritedAccess.accessLabel,
      editability: 'disabled',
      disabledReason: "Independent of this resource's sharing — can't be edited here.",
    });
  }
  // D023 §7.2.3 (version 0.13): the General-access row now carries the SAME
  // two editability kinds every other row has — no more kind 3 ("routed
  // control"). Its own offered ceilings come from
  // `allowedAudienceCeilings[access.audience]` — NEVER `.specific`, which
  // governs named/group rows only (load-bearing: a draft choreo_sheet's
  // `.scope` and `.specific` happen to match today, `['edit','manage']`, but
  // a published Sheet's `.scope`/`.public`/`.link` routinely differ from
  // `.specific` — e.g. anonymous `public`/`link` ceilings are narrower than
  // named-principal ceilings). The same no-escalation fallback (kind 2) that
  // already governs a principal row whose stored ceiling isn't currently
  // offered applies here identically — never render a controlled <select>
  // whose value matches no <option>.
  const generalRowNoEscalationReason = "This access level isn't offered for this resource right now — remove and re-add to change it.";
  if (BROAD_AUDIENCES.includes(access.audience)) {
    const allowedForGeneral = access.allowedAudienceCeilings?.[access.audience] || [];
    const generalOfferable = allowedForGeneral.includes(access.defaultCeiling);
    rows.push({
      key: 'general',
      kind: 'general',
      who: audienceLabel(access, access.audience),
      source: 'General access',
      ceilingText: ceilingLabel(access, access.defaultCeiling),
      editability: generalOfferable ? 'inline' : 'disabled',
      disabledReason: generalOfferable ? undefined : generalRowNoEscalationReason,
      allowedCeilings: allowedForGeneral,
      audience: access.audience,
    });
  }
  const personTier = [];
  const groupTier = [];
  // Mantis #1186 / M001-F0296 case X (no-escalation display contract, D023
  // §5.3): the inline <select> (§7.2.3 kind 1) may only ever OFFER a ceiling
  // the server currently allows granting for `specific` (never CEILINGS
  // itself, which is a client-side enum of everything this build recognises,
  // not a policy). A row whose OWN stored ceiling isn't in that allowed set —
  // `manage` on a deployment that has since withdrawn it, or a wholly unknown
  // future value — must not render an inline <select> at all: a controlled
  // <select> with a value matching no <option> would either show blank
  // (looks like the grant vanished) or coerce to the first entry (a silent,
  // unrequested downgrade). Falling back to the existing "disabled dropdown
  // with a stated reason" pattern (kind 2, already used for pending
  // invitations and the inherited row) keeps the grant visible with its real
  // value and never offers it as, or alongside, a selectable escalation path.
  const allowedForPrincipals = access.allowedAudienceCeilings?.specific || [];
  access.principals.forEach((principal) => {
    const isInvite = principal.principalType === 'invited_identity';
    const ceilingOfferable = allowedForPrincipals.includes(principal.ceiling);
    const row = {
      key: principalKey(principal),
      kind: 'principal',
      principalType: principal.principalType,
      who: principal.displayLabel,
      source: principal.principalType === 'user' ? 'Direct access'
        : principal.principalType === 'group' ? 'Group' : 'Pending invitation',
      ceilingText: ceilingLabel(access, principal.ceiling),
      editability: !isInvite && ceilingOfferable ? 'inline' : 'disabled',
      disabledReason: isInvite
        ? "Can't be changed until the invitation resolves."
        : ceilingOfferable ? undefined : generalRowNoEscalationReason,
      allowedCeilings: allowedForPrincipals,
      principal,
    };
    (principal.principalType === 'group' ? groupTier : personTier).push(row);
  });
  personTier.sort(compareRowsByDisplayLabel);
  groupTier.sort(compareRowsByDisplayLabel);
  return rows.concat(personTier, groupTier);
}

// ---------------------------------------------------------------------------
// Focus-trapped dialog affordance (unchanged mechanics — generic, no
// resourceType coupling, proven accessible behaviour kept as-is). Still used
// by the three remaining confirmation surfaces: remove/cancel, retry
// delivery, and the link Manage (rotate/TTL) dialog.
// ---------------------------------------------------------------------------

function useDialogFocus(open, containerRef, initialFocusRef, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    dialogStack.push(token);
    initialFocusRef.current?.focus();
    const handleKeyDown = (event) => {
      if (dialogStack[dialogStack.length - 1] !== token) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(containerRef.current?.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ) || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = dialogStack.lastIndexOf(token);
      if (index >= 0) dialogStack.splice(index, 1);
    };
  }, [containerRef, initialFocusRef, open]);
}

// ---------------------------------------------------------------------------
// D023 §9.2 per-row provenance: "for any listed person, show WHY they have
// their tier." Only meaningful for a named `user` row — a named `group` row's
// ceiling IS the row (nothing to explain), and access-explain is subject-
// centric (one user at a time), so it cannot explain a group as a subject.
// Unrelated to the §7 layout redesign; unchanged from 0.7.
//
// MUTATION-SENSITIVE BY DESIGN: this renders nothing until api.getAccessExplain
// resolves, then renders text driven by the resolved payload (blocked / direct-
// individual / direct-group). A hardcoded "set individually" string that never
// calls the API would still pass the happy-path test but fails the blocked-
// subject test (ResourceSharePanel.test.js), which is the mutation this guards.
// ---------------------------------------------------------------------------

function ProvenanceLine({ principal, api }) {
  const [explain, setExplain] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (principal.principalType !== 'user' || typeof api?.getAccessExplain !== 'function') return undefined;
    api.getAccessExplain(principal.principalId).then((response) => {
      if (cancelled) return;
      const value = responseData(response);
      if (isObject(value)) setExplain(value);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [api, principal.principalId, principal.principalType]);

  if (principal.principalType === 'group') return <small className="mc-provenance-inline">Configured for this group.</small>;
  if (!explain) return null;
  if (Array.isArray(explain.blocks) && explain.blocks.length > 0) {
    return <small className="mc-provenance-inline mc-provenance-blocked">Blocked — access is denied despite this rule.</small>;
  }
  const direct = (explain.grants || []).find((grant) => grant.kind === 'direct');
  if (!direct) return null;
  if (direct.source?.principalType === 'group') {
    // D023 §9.2 bullet 2 asks for the overridden group's ceiling too when an
    // individual grant is present; that field isn't returned by the engine
    // today (Core issue #56 bullet 5), so we degrade to naming only the
    // source we DO have rather than inventing the rest.
    return <small className="mc-provenance-inline">via {direct.source.label || 'a group'}</small>;
  }
  if (direct.source?.principalType === 'user') return <small className="mc-provenance-inline">set individually</small>;
  return null;
}

// ---------------------------------------------------------------------------
// D023 §7.3/§7.7 — "Add people, groups": one combobox, one input, both
// principal types, always visible beneath the list. Replaces 0.7's three task
// buttons and AddAccessDialog's two mode-switched dialogs. WAI-ARIA 1.2
// combobox-with-listbox-popup pattern.
// ---------------------------------------------------------------------------

function AddPeopleGroupsCombobox({ access, existingKeys, saving, onSearch, onApply, idPrefix }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searched, setSearched] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [chips, setChips] = useState([]);
  const availableCeilings = access.allowedAudienceCeilings.specific || [];
  const [ceiling, setCeiling] = useState(availableCeilings[0] || 'view');
  const [applyError, setApplyError] = useState('');
  const inputRef = useRef(null);
  const sequenceRef = useRef(0);

  const listboxId = `${idPrefix}-listbox`;
  const inputId = `${idPrefix}-input`;
  const trimmed = query.trim();
  const isEmail = EMAIL.test(trimmed);

  useEffect(() => {
    if (trimmed.length < COMBOBOX_MIN_CHARS) {
      setSuggestions([]); setSearched(false); setOpen(false); setHighlighted(-1);
      return undefined;
    }
    const sequence = ++sequenceRef.current;
    const timer = setTimeout(() => {
      onSearch(trimmed, ['user', 'group']).then((items) => {
        if (sequenceRef.current !== sequence) return;
        setSuggestions(items);
      }).catch(() => {
        if (sequenceRef.current !== sequence) return;
        setSuggestions([]);
      }).finally(() => {
        if (sequenceRef.current !== sequence) return;
        setSearched(true);
        setOpen(true);
        setHighlighted(-1);
      });
    }, COMBOBOX_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed, onSearch]);

  const chipKeyFor = (chip) => chip.principalType === 'invited_identity' ? `invited_identity:${chip.email}` : `${chip.principalType}:${chip.principalId}`;

  const duplicateEmail = isEmail && (
    suggestions.some((item) => typeof item.email === 'string' && item.email.toLowerCase() === trimmed.toLowerCase())
    || chips.some((chip) => chip.principalType === 'invited_identity' && chip.email.toLowerCase() === trimmed.toLowerCase())
  );
  const showInvite = isEmail && !duplicateEmail;
  const options = suggestions.map((item) => ({ kind: 'suggestion', item }))
    .concat(showInvite ? [{ kind: 'invite', email: trimmed }] : []);
  const showNoMatches = searched && options.length === 0;
  const listHasContent = open && (options.length > 0 || showNoMatches);

  const addResolvedChip = (principal) => {
    const key = `${principal.principalType}:${principal.principalId}`;
    if (existingKeys.has(key) || chips.some((chip) => chipKeyFor(chip) === key)) return;
    setChips((current) => [...current, { ...principal, state: 'resolved' }]);
    setQuery(''); setOpen(false); setHighlighted(-1);
    inputRef.current?.focus();
  };
  const addInviteChip = (email) => {
    if (chips.some((chip) => chip.principalType === 'invited_identity' && chip.email === email)) return;
    setChips((current) => [...current, { principalType: 'invited_identity', email, displayLabel: email, state: 'pending' }]);
    setQuery(''); setOpen(false); setHighlighted(-1);
    inputRef.current?.focus();
  };
  const removeChip = (index) => setChips((current) => current.filter((_, itemIndex) => itemIndex !== index));

  const selectOption = (option) => {
    if (!option) return;
    if (option.kind === 'suggestion') {
      if (existingKeys.has(`${option.item.principalType}:${option.item.principalId}`)) return;
      addResolvedChip(option.item);
    } else if (option.kind === 'invite') {
      addInviteChip(option.email);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      if (!listHasContent || options.length === 0) return;
      event.preventDefault();
      setHighlighted((index) => Math.min(index < 0 ? 0 : index + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      if (!listHasContent || options.length === 0) return;
      event.preventDefault();
      setHighlighted((index) => (index <= 0 ? 0 : index - 1));
    } else if (event.key === 'Enter') {
      if (highlighted >= 0 && options[highlighted]) {
        event.preventDefault();
        selectOption(options[highlighted]);
      }
    } else if (event.key === 'Escape') {
      if (open) { event.preventDefault(); setOpen(false); setHighlighted(-1); }
    }
  };

  const optionId = (option) => option.kind === 'invite'
    ? `${idPrefix}-option-invite`
    : `${idPrefix}-option-${option.item.principalType}-${option.item.principalId}`;
  const activeDescendant = highlighted >= 0 && options[highlighted] ? optionId(options[highlighted]) : undefined;

  const canApply = chips.length > 0 && availableCeilings.includes(ceiling);
  const submit = async (event) => {
    event.preventDefault();
    setApplyError('');
    if (!canApply) { setApplyError('Add at least one person or group.'); return; }
    const ok = await onApply(chips.map((chip) => ({
      principalType: chip.principalType,
      principalId: chip.principalId,
      email: chip.email,
      displayLabel: chip.displayLabel,
      ceiling,
      ...(chip.principalType === 'invited_identity' ? { expiresAt: expiresAtFromDays(30) } : {}),
    })));
    if (ok) setChips([]);
  };

  return <div className="mc-add-people-groups">
    <form onSubmit={submit}>
      <label className="mc-field" htmlFor={inputId}><span>Add people, groups</span>
        <input
          id={inputId}
          ref={inputRef}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={listHasContent}
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
          value={query}
          placeholder="Add people, groups, or an email address"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
      </label>
      {listHasContent && <ul id={listboxId} role="listbox" aria-label="Suggestions" className="mc-combobox-listbox">
        {options.length > 0 ? options.map((option, index) => {
          const already = option.kind === 'suggestion' && existingKeys.has(`${option.item.principalType}:${option.item.principalId}`);
          return <li
            key={optionId(option)}
            id={optionId(option)}
            role="option"
            aria-selected={index === highlighted}
            aria-disabled={already || undefined}
            className={`mc-combobox-option${index === highlighted ? ' mc-highlighted' : ''}${already ? ' mc-disabled' : ''}`}
            onClick={() => { if (!already) selectOption(option); }}
          >
            {option.kind === 'invite' ? `Invite ${option.email}` : option.item.displayLabel}
            {already && <span className="mc-muted"> Already has access</span>}
          </li>;
        }) : <li role="status" aria-live="polite" className="mc-combobox-status">No matches</li>}
      </ul>}

      {chips.length > 0 && <ul className="mc-recipient-chips" aria-label="People and groups to add">
        {chips.map((chip, index) => <li key={`${chipKeyFor(chip)}-${index}`} className={`mc-chip mc-chip-${chip.state}`}>
          <span>{chip.displayLabel}</span>
          <span className="mc-chip-state">{chip.state === 'resolved' ? 'Resolved' : 'Pending invitation'}</span>
          <button type="button" aria-label={`Remove ${chip.displayLabel}`} onClick={() => removeChip(index)}>×</button>
        </li>)}
      </ul>}

      {chips.length > 0 && <p className="mc-muted mc-chips-unsaved" role="note">Not shared yet — choose Add to share.</p>}
      {chips.length > 0 && <label className="mc-field"><span>What can they do?</span>
        <select aria-label="What can they do" value={ceiling} onChange={(event) => setCeiling(event.target.value)}>
          {availableCeilings.map((value) => <option key={value} value={value}>{ceilingLabel(access, value)}</option>)}
        </select>
        <small>{ceilingCopy(access, ceiling).consequence}</small>
      </label>}
      {applyError && <p className="mc-alert" role="alert">{applyError}</p>}
      {chips.length > 0 && <div className="mc-dialog-actions">
        <button type="submit" className="mc-primary" disabled={saving || !canApply}>{saving ? 'Adding…' : 'Add'}</button>
      </div>}
    </form>
  </div>;
}

// ---------------------------------------------------------------------------
// D023 §7.4/§7.4.1 (version 0.12) — Quick setup: five one-click buttons that
// replace the retired "General access" fieldset. Positioned LAST, along the
// bottom of the panel, below the list and the "Add people, groups" combobox
// (§7.1 item 3, moved there in v0.12 per owner report — see the file-header
// comment). Buttons 1 (Revoke), 3 (Organisation), 4 (Link) and 5 (Group)
// call exactly the functions the retired fieldset already called — no wire
// change. Button 2 (Publish) is the compound M001-F0297 action and only
// renders when the caller (choreo_sheet's SheetPermissionsPanel) supplies a
// `publishControl`; `choreo_media` passes none and never sees a Publish
// button, exactly as it never rendered the old standalone "Publish" section.
// ---------------------------------------------------------------------------

function QuickSetupRow({ access, saving, publishControl, revokeDisabled, idPrefix, onRevoke, onOrganisation, onCreateLink, onPublish, onFocusGroup }) {
  // Owner review of #1194 (2026-08-22): "everyone in the Org can see it" is
  // achievable only when `view` is actually among the offered scope
  // ceilings. A draft Sheet's real shape is `['edit', 'manage']` — no `view`
  // at all, because Core does not offer view-only access to a Sheet with no
  // published revision to view (there is genuinely nothing to see). Taking
  // the lowest RANKED ceiling there still yields `edit`, which would grant
  // every organisation member edit rights on the draft under a button
  // labelled as view-only — a silent one-click privilege escalation. The
  // honest response, matching Link's own already-established disabled+
  // reason treatment exactly, is to refuse the button rather than upgrade
  // the grant.
  const organisationDisabled = !(access.allowedAudienceCeilings.scope || []).includes('view');
  const linkDisabled = (access.allowedAudienceCeilings.link || []).length === 0;
  // §7.4.1 point 1: the ONLY stated disable reason for Publish is missing the
  // `choreography_publish` capability — a caller-supplied "busy" (collab not
  // yet settled) state also disables the button but, like the pre-existing
  // "Publish version…" control it replaces, carries no separate stated
  // reason (unchanged UX, not a new omission).
  const publishNoCapability = Boolean(publishControl) && publishControl.canPublish !== true;
  // Production defect fix (2026-08-23): Core's `allowedAudienceCeilings`
  // only offers `public` when the resource has a safe, anonymous-suitable
  // projection to publish (D023 §8.7.1) — no production choreo_sheet has
  // ever had a `safeProjection`, so `public` was permanently `[]` and
  // Publish's own `replaceAccess({ audience: 'public', ... })` call was
  // unconditionally rejected server-side (400) even with a real draft and
  // real capability. Driven off the server's OWN advertised ceilings, never
  // a hardcoded assumption, exactly like Organisation's/Link's own
  // `view`/non-empty checks above — the button re-enables itself with no
  // code change the instant a Sheet's projection starts advertising a safe
  // view.
  const publishAudienceUnavailable = Boolean(publishControl) && (access.allowedAudienceCeilings.public || []).length === 0;
  const publishDisabled = Boolean(publishControl) && (publishNoCapability || publishAudienceUnavailable || publishControl.busy === true);

  // Owner report (2026-08-22), second pass: hiding the disabled-reason text
  // (the original §7.4.1 `mc-visually-hidden` spans) answered a screen
  // reader but told a sighted user nothing — the owner's own "why is Link
  // not clickable?" is exactly this gap. On a draft/unpublished Sheet, Core's
  // choreographySheetSharingAdapter offers no `link` ceilings and no `view`
  // scope ceiling at all (`choreographySheetSharingAdapter.service.ts`), so
  // Organisation AND Link are routinely BOTH disabled at once for the same
  // underlying reason. Rather than hide the text or print the identical
  // sentence twice side by side, reasons are collected here, deduplicated by
  // exact text, and rendered once each, visibly, below the buttons —
  // `aria-describedby` on every affected button still points at the exact
  // element carrying its reason (shared when the text is shared), so a
  // screen-reader user is told the same thing a sighted user can already
  // see, never something different.
  const reasonTextByButton = {
    revoke: revokeDisabled ? 'Nothing to revoke — access is already Private.' : null,
    publish: publishNoCapability ? "You don't have permission to publish this Sheet."
      : publishAudienceUnavailable ? "This Sheet can't be shared with everyone yet." : null,
    organisation: organisationDisabled ? 'Publish this Sheet first.' : null,
    link: linkDisabled ? 'Publish this Sheet first.' : null,
  };
  const reasonIdByText = new Map();
  const reasons = [];
  Object.values(reasonTextByButton).forEach((text) => {
    if (!text || reasonIdByText.has(text)) return;
    reasonIdByText.set(text, `${idPrefix}-quick-setup-reason-${reasonIdByText.size}`);
    reasons.push({ id: reasonIdByText.get(text), text });
  });
  const reasonIdFor = (key) => {
    const text = reasonTextByButton[key];
    return text ? reasonIdByText.get(text) : undefined;
  };

  return <div className="mc-quick-setup" role="group" aria-label="Quick setup">
    <button type="button" data-quick-setup="revoke" className="mc-quick-setup-button mc-text-danger" aria-describedby={reasonIdFor('revoke')} disabled={saving || revokeDisabled} onClick={onRevoke}>Revoke</button>
    {publishControl && <button type="button" data-quick-setup="publish" className="mc-quick-setup-button" aria-pressed={access.audience === 'public'} aria-describedby={reasonIdFor('publish')} disabled={saving || publishDisabled} onClick={onPublish}>{saving ? 'Working…' : 'Publish'}</button>}
    <button type="button" data-quick-setup="organisation" className="mc-quick-setup-button" aria-pressed={access.audience === 'scope'} aria-describedby={reasonIdFor('organisation')} disabled={saving || organisationDisabled} onClick={onOrganisation}>Organisation</button>
    <button type="button" data-quick-setup="link" className="mc-quick-setup-button" aria-pressed={access.link.active} aria-describedby={reasonIdFor('link')} disabled={saving || linkDisabled} onClick={onCreateLink}>Link</button>
    <button type="button" data-quick-setup="group" className="mc-quick-setup-button" onClick={onFocusGroup}>Group</button>
    {reasons.length > 0 && <div className="mc-quick-setup-reasons">
      {reasons.map((reason) => <p key={reason.id} id={reason.id} className="mc-quick-setup-reason">{reason.text}</p>)}
    </div>}
  </div>;
}

// §7.4 bullet 1 — Revoke's one inline confirmation. Reuses DeleteRuleDialog's
// pattern (focus trap, same dialog classes) with the exact copy that bullet
// specifies, rather than overloading DeleteRuleDialog's own branching (which
// is about ONE row, never "everything").
function RevokeAllDialog({ saving, onClose, onConfirm }) {
  const dialogRef = useRef(null);
  const firstRef = useRef(null);
  useDialogFocus(true, dialogRef, firstRef, onClose);
  const submit = async (event) => { event.preventDefault(); await onConfirm(); };
  return <div className="mc-access-dialog-overlay">
    <form ref={dialogRef} className="mc-dialog mc-access-rule-dialog" role="dialog" aria-modal="true" aria-labelledby="mc-revoke-all-title" onSubmit={submit}>
      <header><h2 id="mc-revoke-all-title">Remove all access?</h2><button type="button" className="mc-icon-button" aria-label="Close remove all access" onClick={onClose}>×</button></header>
      <p ref={firstRef} tabIndex="-1">Everyone below the owner loses access. This cannot be undone from here.</p>
      <div className="mc-dialog-actions"><button type="button" className="mc-quiet" onClick={onClose}>Cancel</button><button type="submit" className="mc-text-danger mc-primary" disabled={saving}>{saving ? 'Removing…' : 'Remove all access'}</button></div>
    </form>
  </div>;
}

// ---------------------------------------------------------------------------
// D023 §7.2.3 / §7.2.4 — the remaining confirmation dialogs. Only three
// survive the redesign: removing a row (or the final row → Private), retrying
// a failed invitation, and managing (rotating/TTL) an active link. Per-row
// role editing (kind 1/2) is inline in the list now, not a dialog.
// ---------------------------------------------------------------------------

// Version 0.13: the General-access row's Remove action now covers `scope`
// and `public`, not only `link` — this dialog's copy is extended to name
// each case honestly rather than reusing `link`-only wording generically.
// The row-level BUTTON label is "Remove" uniformly (§7.2.3's own
// instruction — a different label with no stated reason is what "full of
// inconsistency" describes); this dialog's title/submit copy follows the
// same rule rather than saying "Cancel" underneath a button that now says
// "Remove" — that would reintroduce a smaller version of the exact defect
// being fixed.
//
// Version 0.15 correction (2026-08-24, M001-D023 §8.8.5, T-RES-SHARE-001
// case AF — DEPLOY-GATED, see file-header note): this dialog previously sized
// a "this also wipes N named grants / M invitations, and the Sheet becomes
// Private" warning off the live `access.principals` array, because Core's
// `deleteLink()` genuinely did that unconditionally (verified against
// `origin/main` @ b9c37722, `resourceSharingPhase2.service.ts:886-935` — see
// the superseded comment this replaces, kept in git history, not restated
// here). Core issue #72 (§8.8.5) has since scoped `deleteLink()` to revoke
// ONLY the active link — named grants and pending invitations now survive
// verbatim, and `audience` narrows to `'specific'` if any remain or
// `'private'` only if none do. The warning is now simply false whenever it
// fired with a non-zero count, so the count-sizing logic
// (`namedCount`/`invitationCount`/`principals`) is removed with it, not kept
// dark — a dialog that stops describing a wipe has no reason to still know
// how big the wipe would have been. The one case that genuinely still turns
// the Sheet Private — the link IS the only rule (`lastRule`) — was already a
// separate branch below and needed no change: removing the only access
// mechanism leaves nothing to preserve either way, true before and after
// this fix.
function DeleteRuleDialog({ rule, lastRule, saving, onClose, onDelete }) {
  const dialogRef = useRef(null);
  const firstRef = useRef(null);
  useDialogFocus(true, dialogRef, firstRef, onClose);
  const submit = async (event) => { event.preventDefault(); await onDelete(); };
  const isGeneralLink = rule.kind === 'general' && rule.audience === 'link';
  const isGeneralBroad = rule.kind === 'general' && rule.audience !== 'link';
  const title = lastRule ? 'Make this Private?'
    : isGeneralLink ? 'Remove the link?'
      : isGeneralBroad ? 'Remove general access?'
        : `Remove access for ${rule.who}?`;
  const body = lastRule ? 'Removing the last rule makes this Private. Owner and platform access remains.'
    : isGeneralLink ? 'The link stops working immediately. Named people, groups and pending invitations keep their access.'
      : isGeneralBroad ? 'Only the general-access rule is removed. Named people and groups keep their access.'
        : `Only the access rule for ${rule.who} is removed.`;
  return <div className="mc-access-dialog-overlay">
    <form ref={dialogRef} className="mc-dialog mc-access-rule-dialog" role="dialog" aria-modal="true" aria-labelledby="mc-delete-rule-title" onSubmit={submit}>
      <header><h2 id="mc-delete-rule-title">{title}</h2><button type="button" className="mc-icon-button" aria-label={`Close ${title}`} onClick={onClose}>×</button></header>
      <p ref={firstRef} tabIndex="-1">{body}</p>
      <div className="mc-dialog-actions"><button type="button" className="mc-quiet" onClick={onClose}>Cancel</button><button type="submit" className="mc-text-danger mc-primary" disabled={saving}>{saving ? 'Removing…' : lastRule ? 'Confirm Private' : 'Remove'}</button></div>
    </form>
  </div>;
}

function RetryInvitationDialog({ rule, saving, onClose, onRetry }) {
  const dialogRef = useRef(null);
  const firstRef = useRef(null);
  useDialogFocus(true, dialogRef, firstRef, onClose);
  const submit = async (event) => { event.preventDefault(); await onRetry(); };
  return <div className="mc-access-dialog-overlay">
    <form ref={dialogRef} className="mc-dialog mc-access-rule-dialog" role="dialog" aria-modal="true" aria-labelledby="mc-retry-invitation-title" onSubmit={submit}>
      <header><h2 id="mc-retry-invitation-title">Retry invitation for {rule.who}?</h2><button ref={firstRef} type="button" className="mc-icon-button" aria-label={`Close retry invitation for ${rule.who}`} onClick={onClose}>×</button></header>
      <p>The existing invitation and access rule stay unchanged.</p>
      <div className="mc-dialog-actions"><button type="button" className="mc-quiet" onClick={onClose}>Cancel</button><button type="submit" className="mc-primary" disabled={saving}>{saving ? 'Retrying…' : 'Retry delivery'}</button></div>
    </form>
  </div>;
}

// "Manage" the view-only link — D023 §8.8: default 7-day TTL, adjustable up
// to 90 days, rotate. Reached only via the General-access row's "Manage"
// action now (§7.2.4) — initial link creation happens directly from Quick
// setup's Link button (§7.4, version 0.11), not through this dialog.
function LinkDialog({ access, ttlSupported, saving, notice, onClose, onCreateOrRotate, onCancelLink }) {
  const active = access.link.active;
  const [days, setDays] = useState(DEFAULT_TTL_DAYS);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const dialogRef = useRef(null);
  const firstRef = useRef(null);
  useDialogFocus(true, dialogRef, firstRef, onClose);
  const submitCreate = async (event) => {
    event.preventDefault();
    await onCreateOrRotate(days);
  };
  const submitCancel = async () => {
    if (!confirmCancel) { setConfirmCancel(true); return; }
    await onCancelLink();
  };
  return <div className="mc-access-dialog-overlay">
    <div ref={dialogRef} className="mc-dialog mc-access-rule-dialog" role="dialog" aria-modal="true" aria-labelledby="mc-link-dialog-title">
      <header><h2 id="mc-link-dialog-title">Manage the view-only link</h2><button ref={firstRef} type="button" className="mc-icon-button" aria-label="Close manage view-only link" onClick={onClose}>×</button></header>
      {/* Read-only audit (2026-08-22): "Copy link" used to open this dialog
          silently whenever no secret was cached this session — the same
          "control does something other than what it says" defect the owner
          already flagged for "Change". `notice` states, visibly, WHY this
          dialog opened instead of copying; it is absent (and renders
          nothing) whenever the dialog was opened deliberately via "Manage". */}
      {notice && <p className="mc-alert" role="status">{notice}</p>}
      {active ? <>
        <p>A view-only link is active.</p>
        {access.link.expiresAt && <p><strong>Expires {formatExpiry(access.link.expiresAt)}.</strong></p>}
        {!ttlSupported && <p className="mc-muted">Custom expiry isn't available on this deployment yet.</p>}
        <form onSubmit={submitCreate}>
          <label className="mc-field"><span>Change expiry (days)</span>
            <input type="number" min={MIN_TTL_DAYS} max={MAX_TTL_DAYS} value={days} onChange={(event) => setDays(Number(event.target.value))} disabled={!ttlSupported} />
          </label>
          <div className="mc-dialog-actions">
            <button type="button" className="mc-text-danger" onClick={submitCancel}>{confirmCancel ? 'Confirm cancel link' : 'Cancel link'}</button>
            <button type="submit" className="mc-primary" disabled={saving}>{saving ? 'Working…' : 'Get a new link'}</button>
          </div>
        </form>
      </> : <form onSubmit={submitCreate}>
        <p>Anyone with this link can view — never comment or edit.</p>
        <label className="mc-field"><span>Link expires after (days)</span>
          <input type="number" min={MIN_TTL_DAYS} max={MAX_TTL_DAYS} value={days} onChange={(event) => setDays(Number(event.target.value))} disabled={!ttlSupported} />
          {!ttlSupported && <small>Custom expiry isn't available on this deployment yet; a platform default applies.</small>}
        </label>
        <div className="mc-dialog-actions"><button type="button" className="mc-quiet" onClick={onClose}>Cancel</button><button type="submit" className="mc-primary" disabled={saving}>{saving ? 'Creating…' : 'Create link'}</button></div>
      </form>}
    </div>
  </div>;
}

// D023 §7.4/§7.4.1 ruling (2026-08-24, version 0.14 — see file header): a
// post-creation "Copy the new link" modal used to render here, forcing an
// extra click before the owner could do anything else — retired outright.
// The row's own "Copy link" action (below, in ResourceAccessManager) is the
// one remaining surface for a just-created/rotated secret; it already reads
// the same `linkShareUrl` cache this file always populated on success.

// ---------------------------------------------------------------------------
// Main content — embeddable directly (Sheet's "Sheet access" tab) or inside
// the ResourceSharePanel modal wrapper below (video library).
//
// `api` — every call already bound to one org + one resource by the caller:
//   { getAccess(), replaceAccess(body, idempotencyKey), searchPrincipals(q, types),
//     createLink(body, idempotencyKey), deleteLink(body, idempotencyKey),
//     retryInvitation(invitationId, body), getAccessExplain(subjectUuid) }
// `resourceKey` — a stable string identity (e.g. `${orgId}:${resourceId}`) so
// switching resources resets/reloads state, same as the old component.
// `publishControl` (optional, M001-F0297 / §7.4.1) — supplied only by a
// resource profile with a publish/draft lifecycle (today, only
// `choreo_sheet` via `SheetPermissionsPanel`). Shape:
//   { canPublish, hasDraft, busy, draftRevisionNumber, onPublishDraft() }
// `canPublish` gates whether the Quick-setup Publish button is enabled at
// all (§7.4.1 point 1); `hasDraft` decides only whether `onPublishDraft` is
// called before sharing with everyone (§7.4.1 point 3 — a Sheet that is
// already published with nothing new to publish still runs the sharing
// half). `onPublishDraft` must reject/throw on failure and otherwise
// resolve after the draft has materialised as a published revision — it
// must not itself touch sharing/access state. `publishControl` absent (or
// null) renders no Publish button at all.
// ---------------------------------------------------------------------------

export function ResourceAccessManager({ resourceLabel, resourceKey, api, enabled = true, publishControl = null }) {
  const [storedAccess, setStoredAccess] = useState(null);
  const [loadedResource, setLoadedResource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // `saved` marks an announcement as a server-confirmed write (M004-F0663) so
  // it renders as a VISIBLE "Saved" confirmation, not only a hidden live region.
  const [announcementState, setAnnouncementState] = useState({ text: '', saved: false });
  const announcement = announcementState.text;
  const setAnnouncement = useCallback((text) => setAnnouncementState({ text, saved: false }), []);
  const announceSaved = useCallback((text) => setAnnouncementState({ text, saved: true }), []);
  const errorRef = useRef(null);
  const [deleteRule, setDeleteRule] = useState(null);
  const [retryRule, setRetryRule] = useState(null);
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [ttlSupported, setTtlSupported] = useState(true);
  const [linkShareUrl, setLinkShareUrl] = useState(null);
  // Read-only audit (2026-08-22): "Copy link" silently opened Manage instead
  // when no secret was cached (any session that did not JUST create/rotate
  // the link — including every session after a reload, since linkShareUrl
  // resets on mount) — same defect family as the "Change" button the owner
  // already objected to: a control labelled one thing quietly doing another.
  // `linkDialogNotice` carries a stated, visible reason into LinkDialog for
  // that ONE substitution path; explicit "Manage" clicks and a real link
  // copy both clear it, so the explanation never lingers where it doesn't
  // apply.
  const [linkDialogNotice, setLinkDialogNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const [restoreFocusTick, setRestoreFocusTick] = useState(0);
  const requestSequence = useRef(0);
  // During the render before an effect runs, a resource/api/reload switch must
  // not briefly expose the previous resource's confirmed access or its write
  // controls. A failed newest GET therefore stays explicitly unknown.
  const accessIsCurrent = loadedResource?.resourceKey === resourceKey
    && loadedResource?.api === api
    && loadedResource?.enabled === enabled
    && loadedResource?.reload === reload;
  const access = accessIsCurrent ? storedAccess : null;
  const resourceInputs = { resourceKey, api, enabled, reload };
  const currentResourceContextRef = useRef({ ...resourceInputs, generation: 0 });
  const previousContext = currentResourceContextRef.current;
  if (previousContext.resourceKey !== resourceKey || previousContext.api !== api || previousContext.enabled !== enabled || previousContext.reload !== reload) {
    currentResourceContextRef.current = { ...resourceInputs, generation: previousContext.generation + 1 };
  }
  const resourceContext = currentResourceContextRef.current;
  const isCurrentResource = (expected = resourceContext) => {
    const current = currentResourceContextRef.current;
    return current.resourceKey === expected.resourceKey
      && current.api === expected.api
      && current.enabled === expected.enabled
      && current.reload === expected.reload
      && current.generation === expected.generation;
  };
  const runForResource = (expected, action) => {
    if (!isCurrentResource(expected)) return false;
    action();
    return true;
  };
  // An in-flight write belongs to the resource context that rendered its
  // handler. Do not let its completion seed the next resource's stored view.
  const setAccessForResource = (value, expected = resourceContext) => {
    return runForResource(expected, () => setStoredAccess(value));
  };
  const setResourceError = (value, expected = resourceContext) => runForResource(expected, () => setError(value));
  const setResourceSaving = (value, expected = resourceContext) => runForResource(expected, () => setSaving(value));
  const announceResourceSaved = (value, expected = resourceContext) => runForResource(expected, () => announceSaved(value));
  const closeResourceDialog = (expected = resourceContext) => runForResource(expected, closeDialog);
  useEffect(() => {
    if (!error || !errorRef.current) return;
    errorRef.current.scrollIntoView?.({ block: 'nearest' });
    errorRef.current.focus?.({ preventScroll: true });
  }, [error]);
  const comboIdPrefix = `mc-add-combobox-${String(resourceKey || 'panel').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  // Every dialog-opening control remembers itself here first, so closing any
  // dialog (Escape, Cancel, Apply, Done) returns focus to the exact control
  // that opened it. If that control no longer exists (e.g. the row it lived
  // on was just removed), focus falls back to the "Add people, groups" input
  // — D023 §7.2.3's "after deletion, focus moves to the next row's action or,
  // when none remains, the combobox" intent, implemented at the coarse
  // fallback level rather than tracking the exact next row.
  const returnFocusRef = useRef(null);
  const rememberFocus = (target) => { returnFocusRef.current = target || document.activeElement; };

  useEffect(() => {
    const sequence = ++requestSequence.current;
    setStoredAccess(null);
    setError('');
    setAnnouncement('');
    setDeleteRule(null);
    setRetryRule(null);
    setRevokeConfirmOpen(false);
    setLinkOpen(false);
    setLinkShareUrl(null);
    setSaving(false);
    if (!enabled || !resourceKey || typeof api?.getAccess !== 'function') {
      setLoading(false);
      if (enabled) setError('Access is unavailable. No access changes can be made.');
      return () => { requestSequence.current += 1; };
    }
    setLoading(true);
    api.getAccess()
      .then((response) => {
        const value = sharingAccessFromResponse(response);
        if (requestSequence.current === sequence) {
          setStoredAccess(value);
          setLoadedResource({ resourceKey, api, enabled, reload });
        }
      })
      .catch((cause) => {
        if (requestSequence.current === sequence) setError(serverErrorMessage(cause, 'Access is unavailable. No access changes can be made.'));
      })
      .finally(() => {
        if (requestSequence.current === sequence) setLoading(false);
      });
    return () => { requestSequence.current += 1; };
  }, [api, enabled, reload, resourceKey, setAnnouncement]);

  const rows = useMemo(() => buildAccessRows(access), [access]);
  const grantRows = useMemo(() => rows.filter((row) => row.kind !== 'inherited'), [rows]);
  const isEmpty = grantRows.length === 0;
  const existingKeys = useMemo(() => new Set((access?.principals || []).map(principalKey)), [access]);

  const closeDialog = () => {
    setDeleteRule(null); setRetryRule(null); setRevokeConfirmOpen(false); setLinkOpen(false);
    setLinkDialogNotice('');
    setRestoreFocusTick((value) => value + 1);
  };
  useEffect(() => {
    if (!restoreFocusTick) return undefined;
    if (deleteRule || retryRule || revokeConfirmOpen || linkOpen) return undefined;
    const target = returnFocusRef.current;
    const timer = setTimeout(() => {
      if (target?.isConnected) { target.focus(); return; }
      document.getElementById(`${comboIdPrefix}-input`)?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [comboIdPrefix, deleteRule, linkOpen, restoreFocusTick, retryRule, revokeConfirmOpen]);
  const reloadAfterConflict = (message, expected = resourceContext) => runForResource(expected, () => {
    setError(message); closeDialog(); setReload((value) => value + 1);
  });

  const replaceAccess = async (desired, successMessage) => {
    setSaving(true);
    setError('');
    try {
      const response = await api.replaceAccess({ ...desired, expectedVersion: access.version }, idempotencyKey('access'));
      if (!isCurrentResource()) return false;
      const next = mutationFromResponse(response, access);
      if (changeUnconfirmed(desired, next, access)) {
        console.error('[resource-sharing] write not confirmed by Core', { requested: desired.audience, returnedVersion: next.version, requestId: requestIdOf({ response }) });
        // Refresh inline (not via `reload`, whose effect would erase the
        // banner) so the panel shows Core's real state AND the warning.
        try { setAccessForResource(sharingAccessFromResponse(await api.getAccess())); } catch { /* keep the prior state */ }
        if (!isCurrentResource()) return false;
        closeResourceDialog();
        setResourceError(NOT_CONFIRMED);
        return false;
      }
      if (!setAccessForResource(next)) return false;
      announceResourceSaved(successMessage);
      closeResourceDialog();
      return true;
    } catch (cause) {
      if (!isCurrentResource()) return false;
      if (cause?.response?.status === 409) reloadAfterConflict('Access changed elsewhere. The latest rules were reloaded.');
      else setResourceError(writeFailure(cause, 'Access could not be updated. No changes were applied.'));
      return false;
    } finally {
      setResourceSaving(false);
    }
  };

  // D023 §8.8.1: ttlSeconds on POST/DELETE .../link is optional per the
  // documented shape, but studioaeye-Core MR !433 (the code implementing it)
  // was still Draft when this shipped, and the pre-!433 backend rejects any
  // unrecognised body field with 400 RESOURCE_SHARE_POLICY_INVALID. Feature-
  // detect: try with ttlSeconds first; on that exact rejection (and only that
  // one — any other error still surfaces as a real failure), retry once
  // without it and remember the deployment doesn't support it yet.
  const createOrRotateLink = async (days, successMessage) => {
    setSaving(true);
    setError('');
    const ttlSeconds = Math.min(Math.max(days, MIN_TTL_DAYS), MAX_TTL_DAYS) * DAY_SECONDS;
    const reason = reasonFor(access.link.active ? 'Rotated the view-only link.' : 'Created a view-only link.');
    const attempt = (includeTtl) => api.createLink(
      { expectedVersion: access.version, reason, ...(includeTtl ? { ttlSeconds } : {}) },
      idempotencyKey('link'),
    );
    try {
      let response;
      let usedTtl = ttlSupported;
      try {
        response = await attempt(ttlSupported);
      } catch (cause) {
        if (!isCurrentResource()) return false;
        if (ttlSupported && cause?.response?.data?.error?.code === 'RESOURCE_SHARE_POLICY_INVALID') {
          usedTtl = false;
          response = await attempt(false);
        } else throw cause;
      }
      if (!isCurrentResource()) return false;
      setTtlSupported(usedTtl);
      const value = responseData(response);
      if (!setAccessForResource(mutationFromResponse(response, access))) return false;
      if (typeof value.linkSecret === 'string' && value.linkSecret.length) {
        // Version 0.14 (owner ruling — see file header): no more
        // `LinkResultDialog`. Cache the just-created/rotated secret and
        // close whatever dialog was open (Quick setup's one-click Link
        // never opened one; Manage's rotate did) — the row's own "Copy
        // link" action is the only remaining surface for it, exactly the
        // "from that row I modify what I want" the ruling asks for.
        const url = `${window.location.origin}/choreography/shared/${encodeURIComponent(resourceKey)}#${encodeURIComponent(value.linkSecret)}`;
        setLinkShareUrl({ url, expiresAt: value.expiresAt || null });
        announceResourceSaved(successMessage);
        closeResourceDialog();
      } else {
        setResourceError('The link was already created, but its one-time value cannot be shown again. Get a new link to receive a fresh copy.');
        closeResourceDialog();
      }
      return true;
    } catch (cause) {
      if (!isCurrentResource()) return false;
      if (cause?.response?.status === 409) reloadAfterConflict('Access changed elsewhere. The latest rules were reloaded.');
      else setResourceError(writeFailure(cause, 'The link could not be created. No access changes were made.'));
      return false;
    } finally {
      setResourceSaving(false);
    }
  };

  const deleteLink = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await api.deleteLink({ expectedVersion: access.version, reason: reasonFor('Removed the view-only link.') }, idempotencyKey('link'));
      if (!isCurrentResource()) return;
      if (!setAccessForResource(mutationFromResponse(response, access))) return;
      setLinkShareUrl(null);
      announceResourceSaved('The view-only link was removed.');
      closeResourceDialog();
    } catch (cause) {
      if (!isCurrentResource()) return;
      if (cause?.response?.status === 409) reloadAfterConflict('Access changed elsewhere. The latest rules were reloaded.');
      else setResourceError(writeFailure(cause, 'The link could not be removed. No access changes were made.'));
    } finally {
      setResourceSaving(false);
    }
  };

  const applyAddChips = async (entries) => {
    let principals;
    try { principals = access.principals.map(principalInput); } catch (cause) { setError(cause.message); return false; }
    entries.forEach((entry) => {
      if (entry.principalType === 'invited_identity') {
        principals.push({ principalType: 'invited_identity', email: entry.email, ceiling: entry.ceiling, expiresAt: entry.expiresAt });
      } else {
        principals.push({ principalType: entry.principalType, principalId: entry.principalId, ceiling: entry.ceiling });
      }
    });
    const audience = access.audience === 'private' || access.audience === 'link' ? 'specific' : access.audience;
    return replaceAccess({ audience, defaultCeiling: access.defaultCeiling, principals, reason: reasonFor(`Shared with ${namesList(entries)} — ${ceilingLabel(access, entries[0].ceiling)}.`) },
      `Shared with ${namesList(entries)}.`);
  };

  // D023 §7.2.3 kind 1 — inline role change, no modal.
  const editPrincipal = async (row, ceiling) => {
    let principals;
    try {
      principals = access.principals.map((principal) => principalKey(principal) === row.key
        ? principalInput({ ...principal, ceiling })
        : principalInput(principal));
    } catch (cause) { setError(cause.message); return false; }
    return replaceAccess({ audience: access.audience, defaultCeiling: access.defaultCeiling, principals, reason: reasonFor(`Changed ${row.who}'s access to ${ceilingLabel(access, ceiling)}.`) },
      `${row.who}'s access was updated.`);
  };

  // D023 §7.2.3 kind 1 (version 0.13) — the General-access row's ceiling is
  // now the SAME inline <select> a principal row has: no modal, no routing
  // to Quick setup. Calls the identical `replaceAccess` a named row's
  // <select> already calls, with `principals` passed through UNCHANGED —
  // exactly as `setBroadAudience` below already does when Quick setup
  // CREATES this row. This is also the one call in this file whose
  // audience/ceiling pair the Sheet-specific wrapper
  // (MusicChoreography.js's SheetPermissionsPanel) reads to drive
  // `communityPolicyEffectForBroadCeiling` — that wrapper is generic over
  // every `replaceAccess` call already, so no separate wiring is needed
  // here to make contribute→comment/edit→edit reachable through this
  // dropdown for the first time.
  const editGeneralCeiling = async (ceiling) => replaceAccess({
    audience: access.audience, defaultCeiling: ceiling, principals: access.principals.map(principalInput),
    reason: reasonFor(`Changed general access to ${ceilingLabel(access, ceiling)}.`),
  }, `General access is now ${ceilingLabel(access, ceiling)}.`);

  // §7.4 bullet 1 — Revoke: an UNCONDITIONAL clear (`audience: 'private',
  // principals: []`), a deliberate behavioural change from v0.9/0.10's
  // context-sensitive "Restricted" radio (which kept named principals,
  // landing on `specific`). The owner's own words — "either I'm sharing it
  // or not" — describe this unconditional reset.
  // CORRECTED by D023 version 0.13 (this comment previously claimed "the old
  // nuance — preserve named people when clearing only the broad rule —
  // remains available on each named row's own Remove action" — that was
  // INACCURATE, not merely imprecise, and the design doc now strikes it
  // explicitly): a named row's Remove has only ever removed that ONE named
  // principal, never "clear only the broad rule while preserving named
  // principals" — no control anywhere in the shipped panel performed that
  // second, distinct action before version 0.13. The General-access row's
  // own new uniform Remove action (removeRule below, every audience — not
  // only `link`) is what actually supplies it, for the first time, in this
  // version.
  const revokeAll = async () => replaceAccess({
    audience: 'private', defaultCeiling: 'view', principals: [],
    reason: reasonFor('Revoked all access.'),
  }, 'All access was revoked.');

  // Owner review of #1194: the server's `allowedAudienceCeilings[value]`
  // array is never read by position — see `lowestRankedCeiling`'s own
  // comment. `|| 'view'` only covers the case where the server offers
  // nothing this build recognises at all (forward-compat, §7.6 item 1),
  // never the normal case.
  const setBroadAudience = async (value) => {
    const ceiling = lowestRankedCeiling(access.allowedAudienceCeilings[value]) || 'view';
    return replaceAccess({ audience: value, defaultCeiling: ceiling, principals: access.principals.map(principalInput), reason: reasonFor(`Set access to ${audienceLabel(access, value)}.`) },
      `Access is now ${audienceLabel(access, value)}.`);
  };

  // §7.4.1 / M001-F0297 — the compound Publish action. Resolving the apparent
  // tension between point 1 ("enabled exactly when publishControl.allowed")
  // and point 3 (an already-published Sheet with no draft still runs the
  // sharing half): the button's ENABLEMENT gate is capability-only
  // (`canPublish` — QuickSetupRow's `publishDisabled`); `hasDraft` decides
  // only whether step 1 below runs at all. Step 2 (share with everyone)
  // always runs, matching point 3's "perform only step 2's replaceAccess
  // call" and point 5's framing of Publish as always setting `audience:
  // 'public'`.
  //
  // Partial failure (point 4) deliberately does NOT reuse the shared
  // `replaceAccess`/`reloadAfterConflict` helpers above for this one call.
  // Those helpers unconditionally set their OWN generic message
  // ('Access could not be updated…' / 'Access changed elsewhere…') and, on a
  // 409, bump `reload` — whose effect resets `error` to '' the instant it
  // runs, BEFORE the refetch even starts. Chaining a second setError after
  // that helper returns cannot durably win: the reload effect's own reset
  // fires as its own render pass and erases it just as reliably. Point 4
  // requires ONE banner that actually stays visible ("This Sheet was
  // published, but sharing with everyone could not be completed") while
  // STILL refreshing access state on a 409 (the spirit of
  // `reloadAfterConflict`, not its exact call) — so this inlines the
  // equivalent refresh directly and sets the compound-specific message last.
  const publishAndShare = async () => {
    if (!publishControl) return false;
    setSaving(true);
    setError('');
    if (publishControl.hasDraft) {
      try {
        await publishControl.onPublishDraft();
      } catch (cause) {
        if (!isCurrentResource()) return false;
        setResourceSaving(false);
        setResourceError(writeFailure(cause, 'The Sheet could not be published. No access changes were made.'));
        return false;
      }
    }
    if (!isCurrentResource()) return false;
    const hadDraft = publishControl.hasDraft;
    try {
      const response = await api.replaceAccess(
        {
          audience: 'public', defaultCeiling: 'view', principals: access.principals.map(principalInput),
          reason: reasonFor('Published and shared with everyone.'), expectedVersion: access.version,
        },
        idempotencyKey('access'),
      );
      if (!isCurrentResource()) return false;
      if (!setAccessForResource(mutationFromResponse(response, access))) return false;
      announceResourceSaved('Published and shared with everyone.');
      closeResourceDialog();
      return true;
    } catch (cause) {
      if (!isCurrentResource()) return false;
      // The publish half (already awaited above, if it ran) is never rolled
      // back here — there is no unpublish call anywhere in this contract.
      if (cause?.response?.status === 409) {
        try {
          const fresh = await api.getAccess();
          if (!isCurrentResource()) return false;
          setAccessForResource(sharingAccessFromResponse(fresh));
        } catch { /* keep the current in-memory state if the refresh itself also fails */ }
      }
      setResourceError(hadDraft
        ? 'This Sheet was published, but sharing with everyone could not be completed — reload and try again.'
        : writeFailure(cause, 'Access could not be updated. No changes were applied.'));
      return false;
    } finally {
      setResourceSaving(false);
    }
  };

  // D023 §7.2.3 (version 0.13) — Remove is now offered on the General-access
  // row for EVERY audience (`scope`/`public` included, not only `link`),
  // routed through this SAME generic mechanism named/group rows already use
  // — one code path, not a second one. This corrects the previously
  // UNCONDITIONAL `kind === 'general' → deleteLink()` branch: that was only
  // ever safe because no Remove/Cancel button reached it for `scope`/
  // `public` before this version (confirmed against `main` before this
  // change) — a live defect masked by dead code, not a design this version
  // introduces. `link` keeps its named exception, routed to `deleteLink`,
  // under the same "Remove" label; everything else falls through to the
  // generic `replaceAccess` transition below, which preserves every named
  // principal and every other rule — exactly the property that distinguishes
  // Remove from Revoke (§7.4 bullet 1, an unconditional clear of everything).
  // `link`'s own exception was NOT the same "preserve everyone else"
  // property when this paragraph was written: Core's `deleteLink` (verified
  // against `origin/main` @ b9c37722, `resourceSharingPhase2.service.ts:
  // 886-935`) unconditionally revoked every named grant and invitation on
  // the policy too, then set the resource Private — closer to Revoke's blast
  // radius than to this function's own generic branch.
  // STRUCK by version 0.15 (2026-08-24, M001-D023 §8.8.5, DEPLOY-GATED — see
  // file-header note): Core issue #72 has since scoped `deleteLink()` to the
  // link alone, so `link`'s exception now DOES share the "preserve everyone
  // else" property with the generic branch — the two paths differ only in
  // which endpoint they call, not in blast radius. Left standing, not
  // deleted, per this file's own superseded-ruling convention.
  const removeRule = async () => {
    if (deleteRule.kind === 'general') {
      if (access.audience === 'link') return deleteLink();
      let principals;
      try { principals = access.principals.map(principalInput); } catch (cause) { setError(cause.message); return; }
      const audience = principals.length > 0 ? 'specific' : 'private';
      await replaceAccess({ audience, defaultCeiling: 'view', principals, reason: reasonFor('Removed general access.') },
        'General access was removed.');
      return;
    }
    let principals;
    try {
      principals = access.principals
        .filter((principal) => principalKey(principal) !== deleteRule.key)
        .map(principalInput);
    } catch (cause) { setError(cause.message); return; }
    let audience = access.audience;
    let defaultCeiling = access.defaultCeiling;
    if (audience === 'specific' && principals.length === 0) { audience = 'private'; defaultCeiling = 'view'; }
    await replaceAccess({ audience, defaultCeiling, principals, reason: reasonFor(`Removed ${deleteRule.who}'s access.`) },
      `${deleteRule.who}'s access was removed.`);
  };

  const retryInvitation = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await api.retryInvitation(retryRule.principal.invitationId, { expectedVersion: access.version, reason: reasonFor(`Retried the invitation for ${retryRule.who}.`) });
      if (!isCurrentResource()) return;
      const value = responseData(response);
      if (!isObject(value) || value.invitationId !== retryRule.principal.invitationId) throw new Error('Invalid invitation retry response');
      announceResourceSaved(`The invitation for ${retryRule.who} was queued again.`);
      closeResourceDialog();
      setReload((value2) => value2 + 1);
    } catch (cause) {
      if (!isCurrentResource()) return;
      if (cause?.response?.status === 409) reloadAfterConflict('Access changed elsewhere. The latest rules were reloaded.');
      else setResourceError(writeFailure(cause, 'The invitation could not be retried. Access is unchanged.'));
    } finally {
      setResourceSaving(false);
    }
  };

  const searchPrincipals = async (query, types) => {
    const response = await api.searchPrincipals(query, types);
    const value = responseData(response);
    if (!isObject(value) || !Array.isArray(value.items)) throw new Error('Invalid principal search response');
    return value.items;
  };

  const copyGeneralLink = async (event) => {
    rememberFocus(event.currentTarget);
    if (!linkShareUrl) {
      // The bearer secret is one-time-disclosed (D023 §11 — see the
      // file-header comment): with nothing cached this session, "Copy link"
      // genuinely cannot copy anything. Say so, visibly, before substituting
      // Manage — never swap the action silently.
      const notice = "This link can't be shown again — opening Manage so you can issue a new one.";
      setLinkDialogNotice(notice);
      setAnnouncement(notice);
      setLinkOpen(true);
      return;
    }
    setLinkDialogNotice('');
    try {
      await navigator.clipboard.writeText(linkShareUrl.url);
      setAnnouncement('Link copied.');
    } catch {
      setAnnouncement('Copy failed. Use Manage to get the link.');
    }
  };

  if (!enabled) return null;
  const headingId = `mc-people-with-access-${resourceKey || 'panel'}`;
  const revokeDisabled = Boolean(access) && access.audience === 'private' && access.principals.length === 0 && !access.link.active;
  return <section className="mc-access-rule-manager" aria-labelledby={headingId}>
    <header><h3 id={headingId}>People with access</h3></header>
    {loading && <p role="status">Loading access…</p>}
    {error && <div className="mc-alert mc-access-write-error" role="alert" tabIndex={-1} ref={errorRef}>
      <span className="mc-alert-message">{failureText(error)}</span>
      {typeof error === 'object' && (error.code || error.requestId) && <small className="mc-alert-detail">{[error.code && `Code ${error.code}`, error.requestId && `Request ${error.requestId}`].filter(Boolean).join(' · ')}</small>}
    </div>}
    {access && <>
      <div className="mc-access-rows-wrap"><table className="mc-access-rows-table"><thead><tr><th scope="col">Who</th><th scope="col">Source</th><th scope="col">What they can do</th><th scope="col"><span className="mc-visually-hidden">Actions</span></th></tr></thead><tbody>
        {rows.map((row) => {
          const reasonId = `mc-row-reason-${row.key}`;
          return <tr key={row.key} data-row-kind={row.kind}>
            <td><strong>{row.who}</strong>
              {row.kind === 'principal' && row.principalType !== 'invited_identity' && <ProvenanceLine principal={row.principal} api={api} />}
            </td>
            <td>{row.source || <span className="mc-visually-hidden">Inherited — not a share</span>}</td>
            <td>
              {row.editability === 'inline' && <select aria-label={`${row.who}'s access`} value={row.kind === 'general' ? access.defaultCeiling : row.principal.ceiling} onChange={(event) => (row.kind === 'general' ? editGeneralCeiling(event.target.value) : editPrincipal(row, event.target.value))}>
                {row.allowedCeilings.map((value) => <option key={value} value={value}>{ceilingLabel(access, value)}</option>)}
              </select>}
              {row.editability === 'disabled' && <>
                <select aria-label={`${row.who}'s access`} disabled aria-describedby={reasonId} value="current" onChange={() => {}}>
                  <option value="current">{row.ceilingText}</option>
                </select>
                <small id={reasonId} className="mc-muted">{row.disabledReason}</small>
              </>}
            </td>
            <td><div className="mc-access-row-actions">
              {/* Read-only audit (2026-08-22), same family as the Quick-setup
                  presentation fix: `.mc-text-danger` alone carries no
                  font-weight while `.mc-quiet` alone is bold, so a lone
                  `mc-text-danger` (Remove/Cancel here, previously) rendered
                  LIGHTER than the neutral Retry/Copy/Manage/Change actions
                  beside it — the two most destructive row actions were the
                  only ones NOT bold. `mc-quiet mc-text-danger` together
                  (Revoke's existing, already-correct pairing) restores bold
                  weight with the danger colour, matching every other row
                  action's weight while keeping the danger accent. */}
              {row.kind === 'principal' && <button type="button" className="mc-quiet mc-text-danger" aria-label={`Remove ${row.who}'s access`} onClick={(event) => { rememberFocus(event.currentTarget); setDeleteRule(row); }}>Remove</button>}
              {row.kind === 'principal' && row.principalType === 'invited_identity' && row.principal.deliveryState === 'delivery_failed' && <button type="button" className="mc-quiet" aria-label={`Retry invitation for ${row.who}`} onClick={(event) => { rememberFocus(event.currentTarget); setRetryRule(row); }}>Retry delivery</button>}
              {row.kind === 'general' && row.audience === 'link' && <>
                <button type="button" className="mc-quiet" aria-label="Copy the view-only link" onClick={copyGeneralLink}>Copy link</button>
                <button type="button" className="mc-quiet" aria-label="Manage the view-only link" onClick={(event) => { rememberFocus(event.currentTarget); setLinkDialogNotice(''); setLinkOpen(true); }}>Manage</button>
              </>}
              {/* D023 §7.2.3 (version 0.13): Remove on EVERY General-access
                  row, not only `link`. Labelled "Remove" uniformly — the
                  `link` row's Remove routes to `deleteLink` (via removeRule
                  above) purely to invalidate the token, the same scoped
                  effect the generic `replaceAccess` branch has for every
                  other audience since version 0.15 (M001-D023 §8.8.5,
                  DEPLOY-GATED — see file-header note; STRUCK: this comment
                  previously said `deleteLink` was a materially wider action
                  that also revoked every named grant/invitation and set
                  Private, which was accurate against Core `origin/main` @
                  b9c37722 when written but is not the current design). The
                  two routes still differ only in which endpoint they call,
                  not in blast radius — one label ("Remove") remains correct
                  per the owner's own "full of inconsistency" complaint about
                  a different label with no stated reason. */}
              {row.kind === 'general' && <button type="button" className="mc-quiet mc-text-danger" aria-label={row.audience === 'link' ? 'Remove the view-only link' : 'Remove general access'} onClick={(event) => { rememberFocus(event.currentTarget); setDeleteRule(row); }}>Remove</button>}
            </div></td>
          </tr>;
        })}
        {isEmpty && <tr><td colSpan="4"><span className="mc-muted">Not shared.</span></td></tr>}
      </tbody></table></div>

      <AddPeopleGroupsCombobox access={access} existingKeys={existingKeys} saving={saving} onSearch={searchPrincipals} onApply={applyAddChips} idPrefix={comboIdPrefix} />

      <QuickSetupRow
        access={access}
        saving={saving}
        publishControl={publishControl}
        revokeDisabled={revokeDisabled}
        idPrefix={comboIdPrefix}
        onRevoke={(event) => { rememberFocus(event.currentTarget); setRevokeConfirmOpen(true); }}
        onOrganisation={(event) => { rememberFocus(event.currentTarget); setBroadAudience('scope'); }}
        onCreateLink={(event) => { rememberFocus(event?.currentTarget); createOrRotateLink(DEFAULT_TTL_DAYS, access.link.active ? 'The link was replaced.' : 'A view-only link was created.'); }}
        onPublish={(event) => { rememberFocus(event.currentTarget); publishAndShare(); }}
        onFocusGroup={(event) => { rememberFocus(event.currentTarget); document.getElementById(`${comboIdPrefix}-input`)?.focus(); }}
      />
    </>}
    <div className={announcementState.saved ? 'mc-notice mc-access-saved' : 'mc-visually-hidden'} role="status" aria-live="polite">
      {announcementState.saved && <strong>Saved.</strong>}
      <span>{announcement}</span>
    </div>
    {deleteRule && access && <DeleteRuleDialog rule={deleteRule} lastRule={grantRows.length === 1} saving={saving} onClose={closeDialog} onDelete={removeRule} />}
    {retryRule && access && <RetryInvitationDialog rule={retryRule} saving={saving} onClose={closeDialog} onRetry={retryInvitation} />}
    {revokeConfirmOpen && access && <RevokeAllDialog saving={saving} onClose={closeDialog} onConfirm={revokeAll} />}
    {linkOpen && access && <LinkDialog access={access} ttlSupported={ttlSupported} saving={saving} notice={linkDialogNotice} onClose={closeDialog} onCreateOrRotate={(days) => createOrRotateLink(days, access.link.active ? 'The link was replaced.' : 'A view-only link was created.')} onCancelLink={deleteLink} />}
  </section>;
}

// Right-side panel wrapper — D023 §7: "a Share _resource label_ action opens a
// right-side panel." Used by the video library (WebUI issue #30's new
// choreo_media management entry point, MediaLibraryGrid.js). The Sheet side
// keeps its existing tab-embedded mount of ResourceAccessManager (unchanged
// invocation surface) and does not use this wrapper.
export default function ResourceSharePanel({ open, resourceLabel, resourceKey, api, onClose }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const returnFocusRef = useRef(null);
  useEffect(() => { if (open) returnFocusRef.current = document.activeElement; }, [open]);
  useDialogFocus(open, panelRef, closeRef, onClose);
  useEffect(() => {
    if (open) return undefined;
    const target = returnFocusRef.current;
    setTimeout(() => target?.isConnected && target.focus(), 0);
    return undefined;
  }, [open]);
  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release
  // (both mousedown and mouseup on the overlay itself), not a mousedown-only
  // check — a press that starts on the overlay and is dragged onto the panel
  // before release must not dismiss either. Declared unconditionally (rules of
  // hooks) ahead of the `!open` early return below.
  const overlayDismissHandlers = useDismissOnOutsideClick(onClose);
  if (!open) return null;
  return <div className="mc-share-overlay" {...overlayDismissHandlers}>
    <aside ref={panelRef} className="mc-share-panel" role="dialog" aria-modal="true" aria-labelledby="mc-share-panel-title">
      <header><h2 id="mc-share-panel-title">Share {resourceLabel || 'this item'}</h2><button ref={closeRef} type="button" className="mc-icon-button" aria-label={`Close share ${resourceLabel || 'this item'}`} onClick={onClose}>×</button></header>
      <ResourceAccessManager resourceLabel={resourceLabel} resourceKey={resourceKey} api={api} />
    </aside>
  </div>;
}
